/**
 * mSTEM EXT-02 "Khai báo khối mới" — 2-column Block Builder
 * (Phase 6 / Plan 06-03, MOCK-ONLY / DR-02).
 *
 * Left panel: config form — name / shape dropdown / inputs add-remove list /
 * preset color swatches + custom picker / optional icon. Right panel: the LIVE
 * <BlockPreview> SVG silhouette (re-renders on every config change) + a sandbox
 * area that shows the same preview after "Test trong workspace".
 *
 * Presentational only (props-driven, no Redux, no VM). All user-facing copy via
 * gen.ext.block.* (VN default). NEVER imports the real block engine — the
 * preview is the pure-SVG <BlockPreview> mock (Pitfall 1 hard contract).
 */
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import BlockPreview from './block-preview.jsx';
import {SHAPES, INPUT_TYPES, COLOR_SWATCHES} from '../../lib/mstem-mock/ext-block-types.js';

import styles from './block-builder-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Khai báo khối mới',
        description: 'Title of the block builder (EXT-02)',
        id: 'gen.ext.block.title'
    },
    configHeading: {
        defaultMessage: 'Cấu hình khối',
        description: 'Left-panel heading (EXT-02)',
        id: 'gen.ext.block.configHeading'
    },
    previewHeading: {
        defaultMessage: 'Xem trước',
        description: 'Right-panel heading (EXT-02)',
        id: 'gen.ext.block.previewHeading'
    },
    nameLabel: {
        defaultMessage: 'Tên khối',
        description: 'Block name field label (EXT-02)',
        id: 'gen.ext.block.nameLabel'
    },
    namePlaceholder: {
        defaultMessage: 'VD: bật đèn [MÀU]',
        description: 'Block name placeholder (EXT-02)',
        id: 'gen.ext.block.namePlaceholder'
    },
    shapeLabel: {
        defaultMessage: 'Kiểu khối',
        description: 'Block shape dropdown label (EXT-02)',
        id: 'gen.ext.block.shapeLabel'
    },
    shapeCommand: {
        defaultMessage: 'Lệnh (COMMAND)',
        description: 'Block shape option: command (EXT-02)',
        id: 'gen.ext.block.shape.command'
    },
    shapeReporter: {
        defaultMessage: 'Báo cáo (REPORTER)',
        description: 'Block shape option: reporter (EXT-02)',
        id: 'gen.ext.block.shape.reporter'
    },
    shapeBoolean: {
        defaultMessage: 'Điều kiện (BOOLEAN)',
        description: 'Block shape option: boolean (EXT-02)',
        id: 'gen.ext.block.shape.boolean'
    },
    shapeHat: {
        defaultMessage: 'Sự kiện (HAT)',
        description: 'Block shape option: hat (EXT-02)',
        id: 'gen.ext.block.shape.hat'
    },
    inputsLabel: {
        defaultMessage: 'Tham số đầu vào',
        description: 'Inputs list label (EXT-02)',
        id: 'gen.ext.block.inputsLabel'
    },
    addInput: {
        defaultMessage: '+ Thêm tham số',
        description: 'Add an input row (EXT-02)',
        id: 'gen.ext.block.addInput'
    },
    inputName: {
        defaultMessage: 'Tên',
        description: 'Input row name placeholder (EXT-02)',
        id: 'gen.ext.block.inputName'
    },
    inputTypeNumber: {
        defaultMessage: 'Số',
        description: 'Input type: number (EXT-02)',
        id: 'gen.ext.block.inputType.number'
    },
    inputTypeText: {
        defaultMessage: 'Chữ',
        description: 'Input type: text (EXT-02)',
        id: 'gen.ext.block.inputType.text'
    },
    inputTypeBoolean: {
        defaultMessage: 'Đúng/Sai',
        description: 'Input type: boolean (EXT-02)',
        id: 'gen.ext.block.inputType.boolean'
    },
    inputTypeMenu: {
        defaultMessage: 'Danh sách',
        description: 'Input type: menu (EXT-02)',
        id: 'gen.ext.block.inputType.menu'
    },
    removeInput: {
        defaultMessage: 'Xóa tham số',
        description: 'Remove an input row (EXT-02)',
        id: 'gen.ext.block.removeInput'
    },
    colorLabel: {
        defaultMessage: 'Màu khối',
        description: 'Block color label (EXT-02)',
        id: 'gen.ext.block.colorLabel'
    },
    colorCustom: {
        defaultMessage: 'Tùy chỉnh…',
        description: 'Custom color picker label (EXT-02)',
        id: 'gen.ext.block.colorCustom'
    },
    iconLabel: {
        defaultMessage: 'Biểu tượng (tùy chọn)',
        description: 'Optional block icon label (EXT-02)',
        id: 'gen.ext.block.iconLabel'
    },
    save: {
        defaultMessage: 'Lưu khối',
        description: 'Save block primary CTA (EXT-02)',
        id: 'gen.ext.block.save'
    },
    test: {
        defaultMessage: 'Test trong workspace',
        description: 'Test affordance (EXT-02)',
        id: 'gen.ext.block.test'
    },
    previewEmpty: {
        defaultMessage: 'Khối của bạn sẽ hiển thị tại đây khi bạn nhập tên.',
        description: 'Preview/sandbox empty hint (EXT-02)',
        id: 'gen.ext.block.previewEmpty'
    },
    errName: {
        defaultMessage: 'Vui lòng nhập tên khối.',
        description: 'Validation: empty block name (EXT-02)',
        id: 'gen.ext.block.errName'
    }
    // NOTE: gen.ext.block.saved + .testToast are toast ids owned by
    // lib/alerts/index.jsx (extBlockSaved / extBlockTestAdded) — fired via
    // showAlertWithTimeout in the container, NOT redefined here (would create
    // a duplicate-id conflict at i18n extraction).
});

const SHAPE_LABELS = {
    command: messages.shapeCommand,
    reporter: messages.shapeReporter,
    boolean: messages.shapeBoolean,
    hat: messages.shapeHat
};

const INPUT_TYPE_LABELS = {
    number: messages.inputTypeNumber,
    text: messages.inputTypeText,
    boolean: messages.inputTypeBoolean,
    menu: messages.inputTypeMenu
};

const BlockBuilderModalComponent = props => {
    const {
        intl,
        name,
        shape,
        color,
        inputs,
        nameError,
        showSandbox,
        onName,
        onShape,
        onColor,
        onAddInput,
        onRemoveInput,
        onInputName,
        onInputType,
        onTest,
        onSave,
        onRequestClose
    } = props;

    const previewLabel = (name || '').trim();
    const hasName = previewLabel.length > 0;

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            id="blockBuilderModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                <div className={styles.title}>
                    <FormattedMessage {...messages.title} />
                </div>
                <div className={styles.columns}>

                    {/* ---- LEFT: config panel ---- */}
                    <div className={styles.configPanel}>
                        <div className={styles.panelHeading}>
                            <FormattedMessage {...messages.configHeading} />
                        </div>

                        <label
                            className={styles.fieldLabel}
                            htmlFor="blockName"
                        >
                            <FormattedMessage {...messages.nameLabel} />
                        </label>
                        <input
                            className={classNames(styles.input, {[styles.inputError]: nameError})}
                            id="blockName"
                            placeholder={intl.formatMessage(messages.namePlaceholder)}
                            type="text"
                            value={name}
                            onChange={onName}
                        />
                        {nameError === 'name' && (
                            <div className={styles.errText}>
                                <FormattedMessage {...messages.errName} />
                            </div>
                        )}

                        <label
                            className={styles.fieldLabel}
                            htmlFor="blockShape"
                        >
                            <FormattedMessage {...messages.shapeLabel} />
                        </label>
                        <select
                            className={styles.select}
                            id="blockShape"
                            value={shape}
                            onChange={onShape}
                        >
                            {SHAPES.map(s => (
                                <option
                                    key={s.id}
                                    value={s.id}
                                >
                                    {intl.formatMessage(SHAPE_LABELS[s.id])}
                                </option>
                            ))}
                        </select>

                        <div className={styles.inputsHeader}>
                            <span className={styles.fieldLabel}>
                                <FormattedMessage {...messages.inputsLabel} />
                            </span>
                            <button
                                className={styles.addInputButton}
                                type="button"
                                onClick={onAddInput}
                            >
                                <FormattedMessage {...messages.addInput} />
                            </button>
                        </div>
                        {inputs.map((input, idx) => (
                            <div
                                className={styles.inputRow}
                                key={`input-${idx}`}
                            >
                                <input
                                    className={styles.inputRowName}
                                    data-index={idx}
                                    placeholder={intl.formatMessage(messages.inputName)}
                                    type="text"
                                    value={input.name}
                                    onChange={onInputName}
                                />
                                <select
                                    className={styles.inputRowType}
                                    data-index={idx}
                                    value={input.type}
                                    onChange={onInputType}
                                >
                                    {INPUT_TYPES.map(t => (
                                        <option
                                            key={t.id}
                                            value={t.id}
                                        >
                                            {intl.formatMessage(INPUT_TYPE_LABELS[t.id])}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    aria-label={intl.formatMessage(messages.removeInput)}
                                    className={styles.removeInputButton}
                                    data-index={idx}
                                    title={intl.formatMessage(messages.removeInput)}
                                    type="button"
                                    onClick={onRemoveInput}
                                >
                                    {'×'}
                                </button>
                            </div>
                        ))}

                        <label
                            className={styles.fieldLabel}
                            htmlFor="blockColor"
                        >
                            <FormattedMessage {...messages.colorLabel} />
                        </label>
                        <div className={styles.swatchTray}>
                            {COLOR_SWATCHES.map(swatch => (
                                <button
                                    aria-label={swatch}
                                    className={classNames(styles.swatch, {
                                        [styles.swatchSelected]: swatch === color
                                    })}
                                    data-color={swatch}
                                    key={swatch}
                                    style={{backgroundColor: swatch}}
                                    type="button"
                                    onClick={onColor}
                                />
                            ))}
                        </div>
                        <label
                            className={styles.customColor}
                            htmlFor="blockColorCustom"
                        >
                            <input
                                className={styles.colorInput}
                                id="blockColorCustom"
                                type="color"
                                value={color}
                                onChange={onColor}
                            />
                            <FormattedMessage {...messages.colorCustom} />
                        </label>

                        <label
                            className={styles.fieldLabel}
                            htmlFor="blockIcon"
                        >
                            <FormattedMessage {...messages.iconLabel} />
                        </label>
                    </div>

                    {/* ---- RIGHT: live preview panel ---- */}
                    <div className={styles.previewPanel}>
                        <div className={styles.panelHeading}>
                            <FormattedMessage {...messages.previewHeading} />
                        </div>
                        <div className={styles.previewStage}>
                            <BlockPreview
                                color={color}
                                inputs={inputs}
                                label={hasName ? previewLabel : intl.formatMessage(messages.namePlaceholder)}
                                shape={shape}
                            />
                        </div>
                        <div className={styles.sandbox}>
                            {showSandbox && hasName ? (
                                <BlockPreview
                                    color={color}
                                    inputs={inputs}
                                    label={previewLabel}
                                    shape={shape}
                                />
                            ) : (
                                <div className={styles.previewEmpty}>
                                    <FormattedMessage {...messages.previewEmpty} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---- footer ---- */}
                <div className={styles.footer}>
                    <button
                        className={styles.btnTest}
                        type="button"
                        onClick={onTest}
                    >
                        <FormattedMessage {...messages.test} />
                    </button>
                    <button
                        className={styles.btnSave}
                        type="button"
                        onClick={onSave}
                    >
                        <FormattedMessage {...messages.save} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

BlockBuilderModalComponent.propTypes = {
    color: PropTypes.string.isRequired,
    inputs: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string
    })).isRequired,
    intl: intlShape.isRequired,
    name: PropTypes.string.isRequired,
    nameError: PropTypes.string,
    onAddInput: PropTypes.func.isRequired,
    onColor: PropTypes.func.isRequired,
    onInputName: PropTypes.func.isRequired,
    onInputType: PropTypes.func.isRequired,
    onName: PropTypes.func.isRequired,
    onRemoveInput: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onShape: PropTypes.func.isRequired,
    onTest: PropTypes.func.isRequired,
    shape: PropTypes.string.isRequired,
    showSandbox: PropTypes.bool
};

BlockBuilderModalComponent.defaultProps = {
    nameError: null,
    showSandbox: false
};

export default injectIntl(BlockBuilderModalComponent);
